// Analytics and event tracking for NeuroPlay Fantasy Football
// Simplified implementation without external dependencies

export const AUTH_EVENTS = {
  LOGIN_STARTED: 'login_started',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  REGISTRATION_SUCCESS: 'registration_success',
  PHONE_VERIFICATION_STARTED: 'phone_verification_started',
  PHONE_VERIFICATION_SUCCESS: 'phone_verification_success',
  PHONE_VERIFICATION_FAILED: 'phone_verification_failed',
  AGE_VERIFICATION_STARTED: 'age_verification_started',
  AGE_VERIFICATION_SUCCESS: 'age_verification_success',
  AGE_VERIFICATION_FAILED: 'age_verification_failed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
}

export const ENGAGEMENT_EVENTS = {
  BUTTON_CLICKED: 'button_clicked',
  PAGE_VIEWED: 'page_viewed',
  MODAL_OPENED: 'modal_opened',
  TUTORIAL_STARTED: 'tutorial_started',
  TUTORIAL_COMPLETED: 'tutorial_completed',
}

export const GAME_EVENTS = {
  SQUAD_BUILDING_STARTED: 'squad_building_started',
  SQUAD_BUILDING_COMPLETED: 'squad_building_completed',
  FORMATION_CHANGED: 'formation_changed',
  TRANSFER_MADE: 'transfer_made',
  WELCOME_BONUS_CLAIMED: 'welcome_bonus_claimed',
  TOKENS_PURCHASED: 'tokens_purchased',
}

export const ENABLE_ANALYTICS = process.env.NODE_ENV === 'production'

export class AnalyticsManager {
  private enabled: boolean

  constructor(enabled: boolean = ENABLE_ANALYTICS) {
    this.enabled = enabled
  }

  trackEvent(event: { name: string; properties?: Record<string, any> }) {
    if (!this.enabled) {
      console.log('[Analytics - Dev]', event.name, event.properties)
      return
    }
    console.log('[Analytics]', event.name, event.properties)
  }

  trackPageView(page: string, properties?: Record<string, any>) {
    this.trackEvent({
      name: ENGAGEMENT_EVENTS.PAGE_VIEWED,
      properties: { page, ...properties }
    })
  }

  setUserProperties(properties: Record<string, any>) {
    if (!this.enabled) {
      console.log('[Analytics - Dev] User properties:', properties)
      return
    }
    console.log('[Analytics] User properties:', properties)
  }
}

const analyticsManager = new AnalyticsManager()

export function trackLogin() {
  analyticsManager.trackEvent({
    name: AUTH_EVENTS.LOGIN_STARTED,
  })
}

export function trackAuthSuccess(isNewUser: boolean = false) {
  analyticsManager.trackEvent({
    name: isNewUser ? AUTH_EVENTS.REGISTRATION_SUCCESS : AUTH_EVENTS.LOGIN_SUCCESS,
  })
}

export function trackAuthFailure(error: string) {
  analyticsManager.trackEvent({
    name: AUTH_EVENTS.LOGIN_FAILED,
    properties: { error }
  })
}

export function trackVerificationStarted(type: 'phone' | 'age') {
  const eventName = type === 'phone' ? AUTH_EVENTS.PHONE_VERIFICATION_STARTED : AUTH_EVENTS.AGE_VERIFICATION_STARTED
  analyticsManager.trackEvent({ name: eventName })
}

export function trackVerificationSuccess(type: 'phone' | 'age') {
  const eventName = type === 'phone' ? AUTH_EVENTS.PHONE_VERIFICATION_SUCCESS : AUTH_EVENTS.AGE_VERIFICATION_SUCCESS
  analyticsManager.trackEvent({ name: eventName })
}

export function trackVerificationFailure(type: 'phone' | 'age', error: string) {
  const eventName = type === 'phone' ? AUTH_EVENTS.PHONE_VERIFICATION_FAILED : AUTH_EVENTS.AGE_VERIFICATION_FAILED
  analyticsManager.trackEvent({
    name: eventName,
    properties: { error }
  })
}

export function trackOnboardingComplete(properties?: Record<string, any>) {
  analyticsManager.trackEvent({
    name: AUTH_EVENTS.ONBOARDING_COMPLETED,
    properties
  })
}

export function trackSquadBuildingComplete(squad: any, properties?: Record<string, any>) {
  analyticsManager.trackEvent({
    name: GAME_EVENTS.SQUAD_BUILDING_COMPLETED,
    properties: {
      squadSize: squad?.players?.length || 0,
      formation: squad?.formation,
      ...properties,
    },
  })
}

export function trackWelcomeBonusClaimed(amount: number, properties?: Record<string, any>) {
  analyticsManager.trackEvent({
    name: GAME_EVENTS.WELCOME_BONUS_CLAIMED,
    properties: {
      amount,
      ...properties,
    },
  })
}

export function trackButtonClick(buttonName: string, location: string, properties?: Record<string, any>) {
  analyticsManager.trackEvent({
    name: ENGAGEMENT_EVENTS.BUTTON_CLICKED,
    properties: {
      buttonName,
      location,
      ...properties
    },
  })
}

export function trackPageView(page: string, properties?: Record<string, any>) {
  analyticsManager.trackPageView(page, properties)
}